/*
 * a11y-popup-focus.js  (DJM accessibility remediation)
 * -----------------------------------------------------
 * Requirement (attorney review 2026-07-01): when a pop-up dialog opens, keyboard
 * / screen-reader focus must be moved into it immediately, kept inside it while
 * open (focus trap), and returned to the previous element on close.
 *
 * The two pop-ups on drjudymorgan.com are injected by THIRD-PARTY APPS whose
 * internal JS we cannot edit:
 *   1. Cookie consent  -> Avada Cookies  (.Avada-CookiesFull_bar[role="dialog"])
 *   2. 10% off flyout  -> Klaviyo form   (div[role="dialog"][aria-label="FLYOUT Form"])
 * We manage focus for them from the theme layer only (never alter their markup).
 *
 * v2 (2026-07-01): fix stuck focus. These apps often REMOVE the dialog from the
 * DOM on dismiss (e.g. cookie "Decline"), so a scan that only looks at still-
 * present nodes never fired the cleanup -> the Tab trap stayed bound to a
 * detached node and swallowed every Tab. Now we track handled dialogs in a real
 * list, reconcile ones that vanished/hid, and the trap self-releases if its
 * dialog is gone.
 *
 * v3 (2026-07-01): fix FROZEN Tab when TWO popups are open at once (flyout +
 * cookie bar). Each dialog binds its own capture keydown trap; with both open,
 * both fired on every Tab and each preventDefault'd + refocused its own dialog,
 * fighting each other so Tab never advanced. Now only the top-of-stack dialog
 * (most-recently-opened, still visible) traps Tab; lower dialogs yield.
 */
(function () {
  'use strict';

  var SELECTORS = [
    '.Avada-CookiesFull_bar[role="dialog"]',
    '.Avada-CookiesFull_bar',
    'div[role="dialog"][aria-label="FLYOUT Form"]',
    '[class*="kl-private-reset-css"][role="dialog"]'
  ];

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  // Dialogs we are currently managing (real array so we can reconcile removals).
  var active = [];                 // [{ el, handler, returnFocus }]
  var seen = [];                   // dialogs we've already opened (avoid re-trigger while visible)

  function entryFor(el) {
    for (var i = 0; i < active.length; i++) if (active[i].el === el) return active[i];
    return null;
  }

  // The dialog that currently OWNS the Tab trap = the most-recently-opened one
  // that is still connected + visible (top of the stack). When two popups are
  // open at once (e.g. Klaviyo flyout + Avada cookie bar), only this one traps
  // Tab; the others yield so their handlers don't fight over focus and freeze
  // Tab entirely (v3 fix, 2026-07-01).
  function topDialog() {
    for (var i = active.length - 1; i >= 0; i--) {
      if (active[i].el.isConnected && isVisible(active[i].el)) return active[i].el;
    }
    return null;
  }

  function isVisible(el) {
    if (!el || !el.isConnected) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function focusableIn(dialog) {
    if (!dialog || !dialog.isConnected) return [];
    return Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), isVisible);
  }

  function trapFor(dialog) {
    var handler = function (e) {
      if (e.key !== 'Tab') return;
      // Self-heal: if this dialog is gone/hidden, release ourselves and let Tab work.
      if (!dialog.isConnected || !isVisible(dialog)) {
        document.removeEventListener('keydown', handler, true);
        closeDialog(dialog);
        return;
      }
      // Stacked-popup ownership: if another managed dialog is on top of us, do
      // NOT trap — let the top dialog's handler manage Tab. Without this, two
      // open dialogs each preventDefault + refocus each other and Tab freezes.
      if (topDialog() !== dialog) return;
      var items = focusableIn(dialog);
      if (!items.length) return; // nothing to trap; don't block Tab
      var first = items[0];
      var last = items[items.length - 1];
      var a = document.activeElement;
      if (e.shiftKey) {
        if (a === first || !dialog.contains(a)) { e.preventDefault(); last.focus(); }
      } else {
        if (a === last || !dialog.contains(a)) { e.preventDefault(); first.focus(); }
      }
    };
    return handler;
  }

  function openDialog(dialog) {
    if (entryFor(dialog)) return;              // already managing it
    if (seen.indexOf(dialog) !== -1 && !dialog.isConnected) seen.splice(seen.indexOf(dialog), 1);

    var returnFocus = (document.activeElement && document.activeElement !== document.body)
      ? document.activeElement : null;

    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    var items = focusableIn(dialog);
    var target = items.length ? items[0] : dialog;
    window.requestAnimationFrame(function () {
      if (!dialog.isConnected) return;
      try { target.focus({ preventScroll: false }); } catch (e) { try { target.focus(); } catch (e2) {} }
    });

    var handler = trapFor(dialog);
    document.addEventListener('keydown', handler, true);
    active.push({ el: dialog, handler: handler, returnFocus: returnFocus });
    if (seen.indexOf(dialog) === -1) seen.push(dialog);
  }

  function closeDialog(dialog) {
    var idx = -1;
    for (var i = 0; i < active.length; i++) if (active[i].el === dialog) { idx = i; break; }
    if (idx === -1) return;
    var entry = active[idx];
    active.splice(idx, 1);

    document.removeEventListener('keydown', entry.handler, true);

    // Where should focus go now?
    // 1) If another managed dialog is still open, move focus into it.
    var other = null;
    for (var j = active.length - 1; j >= 0; j--) {
      if (active[j].el.isConnected && isVisible(active[j].el)) { other = active[j].el; break; }
    }
    if (other) {
      var f = focusableIn(other);
      try { (f.length ? f[0] : other).focus(); } catch (e) {}
      return;
    }
    // 2) Otherwise restore focus to whatever had it before this dialog opened.
    if (entry.returnFocus && entry.returnFocus.isConnected && isVisible(entry.returnFocus)) {
      try { entry.returnFocus.focus(); return; } catch (e) {}
    }
    // 3) Last resort: send focus to the skip-to-content link (logical top of the
    //    page) so keyboard users continue Tab-ing INTO the page content, instead
    //    of landing on nothing (which makes browsers resume Tab from their own
    //    chrome / address bar).
    var skip = document.querySelector('.skip-link[href="#main-content"], [data-ce-role="skip"], a.skip-link');
    if (skip) {
      try { skip.focus({ preventScroll: true }); return; } catch (e) { try { skip.focus(); return; } catch (e2) {} }
    }
    // 4) Absolute fallback: blur whatever is focused.
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      try { document.activeElement.blur(); } catch (e) {}
    }
  }

  function scan() {
    // Open newly-visible dialogs.
    SELECTORS.forEach(function (sel) {
      var nodes = document.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) {
        if (isVisible(nodes[i])) openDialog(nodes[i]);
      }
    });
    // Reconcile: close any managed dialog that has been removed or hidden.
    active.slice().forEach(function (entry) {
      if (!entry.el.isConnected || !isVisible(entry.el)) closeDialog(entry.el);
    });
    // Forget dismissed dialogs so they can re-trigger if the app re-shows them.
    seen = seen.filter(function (d) { return d.isConnected; });
  }

  var scheduled = false;
  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () { scheduled = false; scan(); });
  }

  function init() {
    scan();
    var mo = new MutationObserver(scheduleScan);
    mo.observe(document.documentElement, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['style', 'class', 'aria-hidden', 'hidden']
    });
    var ticks = 0;
    var iv = setInterval(function () { scan(); if (++ticks > 60) clearInterval(iv); }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
